import { useState } from "react";
import { motion } from "framer-motion";
import { CreditCard, QrCode, Heart, ArrowRight, X, CheckCircle } from "lucide-react";

export default function Giving() {
  const [showQRModal, setShowQRModal] = useState(false);
  const [copiedText, setCopiedText] = useState("");

  const zelleInfo = {
    phone: "+18643267378", // Substituir pelo número real
    name: "PIB Greenville",
    message: "Dízimo/Oferta - PIBG"
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedText(text);
    setTimeout(() => setCopiedText(""), 2000);
  };

  const generateZelleUrl = () => {
    return `https://zellepay.com/recipients/${zelleInfo.phone.replace('+', '')}`;
  };

  return (
    <>
      <section id="giving-section" className="py-24 sm:py-32 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05] tracking-tight mb-8">
                Semeie na <span className="text-[#D4AF37]">obra de Deus</span>.
              </h2>
              <p className="text-xl text-white/80 leading-relaxed mb-12 max-w-2xl mx-auto">
                Sua generosidade faz a diferença. Cada contribuição ajuda a levar o Evangelho, 
                cuidar de famílias e transformar vidas em Greenville e além.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
              {/* Dízimo */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="bg-[#0A0A0A] border border-white/5 rounded-xl p-8 text-center group hover:border-[#D4AF37]/30 transition-all duration-300"
              >
                <div className="w-16 h-16 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-[#D4AF37]/20 transition-colors">
                  <Heart className="text-[#D4AF37]" size={32} />
                </div>
                <h3 className="font-serif-display text-2xl text-white mb-4">Dízimo</h3>
                <p className="text-white/70 leading-relaxed mb-6">
                  Reconhecimento que tudo vem de Deus e devolvendo os 10% com fé e gratidão.
                </p>
                <div className="text-sm text-white/50">
                  "Trazei todos os dízimos à casa do tesouro..." <br />
                  <span className="text-[#D4AF37]">Malaquias 3:10</span>
                </div>
              </motion.div>

              {/* Ofertas */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="bg-[#0A0A0A] border border-white/5 rounded-xl p-8 text-center group hover:border-[#D4AF37]/30 transition-all duration-300"
              >
                <div className="w-16 h-16 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-[#D4AF37]/20 transition-colors">
                  <CreditCard className="text-[#D4AF37]" size={32} />
                </div>
                <h3 className="font-serif-display text-2xl text-white mb-4">Ofertas</h3>
                <p className="text-white/70 leading-relaxed mb-6">
                  Contribuição espontânea de gratidão pelas bênçãos e livramentos de Deus.
                </p>
                <div className="text-sm text-white/50">
                  "Cada um contribua segundo propôs no coração..." <br />
                  <span className="text-[#D4AF37]">2 Coríntios 9:7</span>
                </div>
              </motion.div>

              {/* Missões */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="bg-[#0A0A0A] border border-white/5 rounded-xl p-8 text-center group hover:border-[#D4AF37]/30 transition-all duration-300"
              >
                <div className="w-16 h-16 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-[#D4AF37]/20 transition-colors">
                  <QrCode className="text-[#D4AF37]" size={32} />
                </div>
                <h3 className="font-serif-display text-2xl text-white mb-4">Missões</h3>
                <p className="text-white/70 leading-relaxed mb-6">
                  Apoio a projetos missionários e evangelismo local e internacional.
                </p>
                <div className="text-sm text-white/50">
                  "Ide por todo o mundo e pregai o evangelho..." <br />
                  <span className="text-[#D4AF37]">Marcos 16:15</span>
                </div>
              </motion.div>
            </div>

            {/* Botão Principal de Ação */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-center"
            >
              <button
                onClick={() => setShowQRModal(true)}
                className="inline-flex items-center gap-3 bg-[#D4AF37] text-black font-semibold px-8 py-4 text-lg hover:bg-[#B3932E] transition-all duration-300 group"
              >
                <QrCode size={20} />
                Contribuir via Zelle
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </button>
              <p className="mt-4 text-sm text-white/50">
                Clique para ver o QR Code e informações de pagamento
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Modal QR Code */}
      {showQRModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setShowQRModal(false)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-[#0A0A0A] border border-white/10 rounded-2xl p-8 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-serif-display text-2xl text-white">Contribuir via Zelle</h3>
              <button
                onClick={() => setShowQRModal(false)}
                className="text-white/60 hover:text-white transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* QR Code Real */}
            <div className="bg-white rounded-xl p-6 mb-6 text-center">
              <img
                src="/qrcode-zelle.png"
                alt="QR Code para doações via Zelle"
                className="w-48 h-48 mx-auto mb-4"
              />
              <p className="text-gray-600 text-sm">
                Escaneie para doar via Zelle
              </p>
            </div>

            {/* Informações */}
            <div className="space-y-4 mb-6">
              <div className="bg-[#050505] rounded-lg p-4">
                <label className="text-xs text-white/50 block mb-1">Número Zelle</label>
                <div className="flex items-center justify-between">
                  <span className="text-white font-mono">{zelleInfo.phone}</span>
                  <button
                    onClick={() => copyToClipboard(zelleInfo.phone)}
                    className="text-[#D4AF37] hover:text-[#B3932E] transition-colors"
                  >
                    {copiedText === zelleInfo.phone ? <CheckCircle size={16} /> : "Copiar"}
                  </button>
                </div>
              </div>

              <div className="bg-[#050505] rounded-lg p-4">
                <label className="text-xs text-white/50 block mb-1">Nome do Beneficiário</label>
                <div className="flex items-center justify-between">
                  <span className="text-white">{zelleInfo.name}</span>
                  <button
                    onClick={() => copyToClipboard(zelleInfo.name)}
                    className="text-[#D4AF37] hover:text-[#B3932E] transition-colors"
                  >
                    {copiedText === zelleInfo.name ? <CheckCircle size={16} /> : "Copiar"}
                  </button>
                </div>
              </div>

              <div className="bg-[#050505] rounded-lg p-4">
                <label className="text-xs text-white/50 block mb-1">Memo (Opcional)</label>
                <div className="flex items-center justify-between">
                  <span className="text-white text-sm">{zelleInfo.message}</span>
                  <button
                    onClick={() => copyToClipboard(zelleInfo.message)}
                    className="text-[#D4AF37] hover:text-[#B3932E] transition-colors"
                  >
                    {copiedText === zelleInfo.message ? <CheckCircle size={16} /> : "Copiar"}
                  </button>
                </div>
              </div>
            </div>

            {/* Botão de Ação */}
            <div className="flex gap-3">
              <a
                href={generateZelleUrl()}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 bg-[#D4AF37] text-black font-semibold py-3 px-4 rounded-lg hover:bg-[#B3932E] transition-colors text-center"
              >
                Abrir Zelle
              </a>
              <button
                onClick={() => setShowQRModal(false)}
                className="flex-1 bg-white/10 text-white font-semibold py-3 px-4 rounded-lg hover:bg-white/20 transition-colors"
              >
                Fechar
              </button>
            </div>

            {/* Texto Informativo */}
            <div className="mt-6 pt-6 border-t border-white/10">
              <p className="text-xs text-white/50 leading-relaxed">
                Sua contribuição é 100% dedutível para fins fiscais. 
                Ao contribuir, você está investindo no Reino de Deus e na transformação de vidas.
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </>
  );
}
